import { Box, Checkbox, FormGroup, Typography } from '@mui/material';
import { CaseDetails, CaseNote, Kpi } from '@oh-vcp/components-common/src/models/case/CaseDetails';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  EconsultProviderTypes,
  EconsultUserRoles,
  NoteActionTypes,
  NoteParticipantStatusReasons,
  NoteStatusCodes,
  NoteStatusReasons,
  NoteTypes
} from '@oh-vcp/components-common';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CheckBoxOptionControl,
  EconsultTextField,
  useNotifierContext,
  EconsultActionButton
} from '@oh-vcp/components-ui';
import { useQueryClient } from 'react-query';
import { useEconsultCaseDetailsContext } from '../econsultCaseDetailsContext/EconsultCaseDetailsContext';
import CaseService from '../services/caseService';
import { useAuth } from '../auth/AuthContext';
import { useEconsultTabContext } from '../econsultTabContext/EconsultTabContext';
import NotificationsConfig from '../notifier/NotificationsConfig';
import { NotificationFactory } from '../notifier/NotificationFactory';
import EconsultUserService from '../services/econsultUserService';
import useAssignerPrograms from '../services/useAssignerPrograms';
import EconsultDraftLostWarningDialog from '../econsultDraftLostWarningDialog/EconsultDraftLostWarningDialog';

interface EconsultCaseCompleteProps {
  actionType: 'complete' | 'cancel';
  draftNote?: CaseNote;
  isDraftChanged: () => boolean | false;
  contentsChanged?: (isChanged: boolean) => void;
}

const validFieldMessageType = {
  display: 'none'
};
const inVaildFieldMessageType = {
  display: 'block',
  color: 'red',
  fontSize: '0.75rem'
};

const EconsultCaseComplete: FC<EconsultCaseCompleteProps> = ({
  actionType,
  draftNote,
  isDraftChanged,
  contentsChanged
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { addNotification, removeAllErrorNotifications } = useNotifierContext();
  const { providerType, statusType } = useParams();
  const { user } = useAuth();
  const { data: assignerPrograms } = useAssignerPrograms();
  const {
    caseDetails,
    setCaseDetails,
    userRole,
    setDraftNote,
    navigationAction,
    setNavigationAction,
    abortAllUploads
  } = useEconsultCaseDetailsContext();
  const queryClient = useQueryClient();
  const { setSelectedTab } = useEconsultTabContext();
  const [specialistFeedback, setSpecialistFeedback] = useState<string>(
    draftNote?.noteContent || ''
  );
  const [programFeedback, setProgramFeedback] = useState<string>('');
  const [feedbackConsent, setFeedbackConsent] = useState<boolean>(false);
  const [contactConsent, setContactConsent] = useState<boolean>(false);
  const [isCompleteBtnDisabled, setIsCompleteBtnDisabled] = useState<boolean>(false);
  const [programFeedbackErrorMessageStyle, setProgramFeedbackErrorMessageStyle] =
    useState(validFieldMessageType);
  const [specialistFeedbackChanged, setSpecialistFeedbackChanged] = useState<boolean>(false);
  const [isDraftWarningDialogOpen, setIsDraftWarningDialogOpen] = useState<boolean>(false);
  const isInitialized = useRef(false);
  const isAssignerCancel = useMemo(() => {
    if (!caseDetails) return false;
    return (
      EconsultUserService.isDepartmentalOrTriageAssignerAction(
        caseDetails,
        userRole,
        assignerPrograms
      ) || EconsultUserService.isTriageRedirectConsultAction(caseDetails, userRole)
    );
  }, [caseDetails, userRole, assignerPrograms]);
  const isTriage = userRole && userRole.indexOf(NoteTypes.triageAdmin) !== -1;

  const handleBeforeUnload = (event: any) => {
    if (window.location.toString().indexOf('logout') > -1) {
      return;
    }
    event.preventDefault();
    event.returnValue = ''; // Required for Chrome and Firefox
    return ''; // Required for Safari
  };
  const handlePopState = (event: PopStateEvent) => {
    console.log(`popstate: ${event}`);
    if (caseDetails && isDraftChanged()) {
      setIsDraftWarningDialogOpen(true);
    }
  };
  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (specialistFeedbackChanged) {
      window.addEventListener('beforeunload', handleBeforeUnload);
      window.addEventListener('popstate', handlePopState);
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, [specialistFeedbackChanged]);
  useEffect(() => {
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  useEffect(() => {
    if (!contentsChanged) return;
    if (specialistFeedback || programFeedback || contactConsent || feedbackConsent) {
      contentsChanged(true);
    } else {
      contentsChanged(false);
    }
  }, [specialistFeedback]);

  const getFinishNoteId = () => {
    const curDraftNote = caseDetails?.notes?.find(
      (n) =>
        n.actionTypeCd ===
        (actionType === 'complete' ? NoteActionTypes.finish : NoteActionTypes.cancel)
    );
    return curDraftNote ? curDraftNote.noteId : undefined;
  };
  const getDrafFinishtNote = () => {
    const noteId = getFinishNoteId();
    return {
      ownerId: user?.userid as number,
      noteId,
      noteTypeCd: NoteTypes.referrer,
      actionTypeCd: NoteActionTypes.finish,
      caseId: caseDetails?.caseId,
      statusCd: NoteStatusCodes.draft,
      kpis: undefined,
      noteContent: specialistFeedback,
      attachments: []
    };
  };

  const getCancelNote = () => {
    return {
      noteId: undefined,
      ownerId: user?.userid as number,
      noteTypeCd: isTriage ? NoteTypes.triageAdmin : NoteTypes.referrer,
      actionTypeCd: NoteActionTypes.cancel,
      caseId: caseDetails?.caseId,
      statusCd: NoteStatusCodes.draft
    };
  };

  const getCaseDraftNotes = async (caseId: number | undefined) => {
    if (!caseId) {
      return undefined;
    }
    const caseInfo = await CaseService.getCaseDetails(caseId);
    const noteType =
      userRole === EconsultProviderTypes.consultant.toUpperCase() ||
      userRole === EconsultUserRoles.consultantDelegate
        ? NoteTypes.consultant
        : NoteTypes.referrer;
    const draftNotes: CaseNote[] = [];
    if (caseInfo && caseInfo.data && caseInfo.data.notes) {
      caseInfo.data.notes.forEach((note) => {
        if (
          note.statusCd &&
          note.statusCd === NoteStatusCodes.draft &&
          (noteType === note.noteTypeCd || (note.noteTypeCd === NoteTypes.triageAdmin && isTriage))
        ) {
          draftNotes.push(note);
        }
      });
    }
    return draftNotes;
  };

  const handleDraftSaved = async () => {
    const caseDraftNotes = await getCaseDraftNotes(caseDetails?.caseId);
    const noteId =
      caseDraftNotes && caseDraftNotes.length > 0 ? caseDraftNotes[0].noteId : undefined;
    const draftNoteToUpsert = actionType === 'complete' ? getDrafFinishtNote() : getCancelNote();
    if (!draftNoteToUpsert.noteId && noteId) {
      draftNoteToUpsert.noteId = noteId;
    }
    const upsertedNote = await CaseService.upsertCaseNote(draftNoteToUpsert);
    setCaseDetails({
      ...caseDetails,
      notes: [...(caseDetails?.notes || []), upsertedNote]
    });
  };

  useEffect(() => {
    if (!isInitialized.current && caseDetails) {
      handleDraftSaved();
      isInitialized.current = true;
    }
  }, [caseDetails]);

  const kpis: Kpi[] = [
    {
      kpiQuestion:
        actionType === 'cancel'
          ? 'Cancelled case feedback to the eConsult Program'
          : 'Completed case feedback to the eConsult Program'
    },
    {
      kpiQuestion:
        'I consent to be contacted by the eConsult Centre of Excellence or Ontario Health in response to my feedback.',
      isCheckbox: true
    },
    {
      kpiQuestion: 'I consent to have my feedback shared with the specialist.',
      isCheckbox: true
    }
  ];
  const getNotificationMessage = () => {
    let message = { message: '' };
    switch (actionType) {
      case 'cancel':
        message = NotificationsConfig.econsultCancelConsult.success;
        break;
      case 'complete':
      default:
        message = NotificationsConfig.econsultCompleteConsult.success;
        break;
    }

    return message;
  };

  const handleAssignerCancel = async () => {
    setIsCompleteBtnDisabled(true);
    let noteToCancel: CaseNote | undefined;
    const cancelledNote = caseDetails?.notes?.find(
      (n) => n.actionTypeCd === NoteActionTypes.cancel
    );
    if (cancelledNote) {
      noteToCancel = cancelledNote;
    }
    if (!noteToCancel) {
      noteToCancel = await CaseService.upsertCaseNote({
        ownerId: user?.userid as number,
        noteTypeCd: NoteTypes.triageAdmin,
        actionTypeCd: NoteActionTypes.cancel,
        caseId: caseDetails?.caseId,
        statusCd: NoteStatusCodes.active
      });
    } else {
      noteToCancel.noteTypeCd = NoteTypes.triageAdmin;
      noteToCancel.statusCd = NoteStatusCodes.active;
    }
    const updatedCaseDetails: CaseDetails = {
      ...caseDetails,
      statusCd: NoteStatusCodes.closed,
      statusReason: NoteStatusReasons.cancelled,
      participant: {
        ...caseDetails?.participant,
        statusCd: NoteStatusCodes.closed,
        statusReason: undefined,
        ...(caseDetails?.participant?.triageStatusCd && {
          triageStatusCd: NoteStatusCodes.closed
        })
      },
      notes: [noteToCancel]
    };
    setCaseDetails(updatedCaseDetails);
    removeAllErrorNotifications();
    CaseService.upsertCaseDetails(updatedCaseDetails);
    addNotification(NotificationFactory.createNotification('success', getNotificationMessage()));
    setIsCompleteBtnDisabled(false);
    setSelectedTab(undefined);
    setTimeout(() => {
      if (providerType === 'search') {
        queryClient.invalidateQueries(['caseSearch']);
      } else {
        queryClient.invalidateQueries(['caseResults']);
      }
    }, 1000);
    navigate(`/case/${providerType?.toLowerCase()}/${statusType?.toLowerCase()}`);
  };

  const handleComplete = async () => {
    if (contactConsent && !programFeedback) {
      setProgramFeedbackErrorMessageStyle(inVaildFieldMessageType);
      return;
    }

    if (
      ((contactConsent || feedbackConsent) && programFeedback) ||
      (!contactConsent && !feedbackConsent)
    ) {
      setIsCompleteBtnDisabled(true);

      kpis[1].kpiAnswer = Number(contactConsent);
      kpis[2].kpiAnswer = Number(feedbackConsent);
      kpis[1].kpiComment = contactConsent ? 'Yes' : 'No';
      kpis[2].kpiComment = feedbackConsent ? 'Yes' : 'No';
      kpis[0].kpiComment = programFeedback;

      let noteToCancelOrComplete: CaseNote | undefined;
      const finishNoteId = actionType === 'complete' ? getFinishNoteId() : undefined;
      if (actionType === 'cancel') {
        const cancelledNote = caseDetails?.notes?.find(
          (n) => n.actionTypeCd === NoteActionTypes.cancel
        );

        if (cancelledNote) {
          if (isTriage) {
            cancelledNote.noteTypeCd = NoteTypes.triageAdmin;
          }
          noteToCancelOrComplete = cancelledNote;
        }
      }

      if (!noteToCancelOrComplete) {
        noteToCancelOrComplete = await CaseService.upsertCaseNote({
          ownerId: user?.userid as number,
          noteId: finishNoteId,
          noteTypeCd:
            isTriage && actionType !== 'complete' ? NoteTypes.triageAdmin : NoteTypes.referrer,
          actionTypeCd: actionType === 'complete' ? NoteActionTypes.finish : NoteActionTypes.cancel,
          caseId: caseDetails?.caseId,
          statusCd: NoteStatusCodes.active
        });
      } else if (actionType === 'cancel') {
        noteToCancelOrComplete.statusCd = NoteStatusCodes.active;
        await CaseService.upsertCaseNote(noteToCancelOrComplete);
      }
      if (specialistFeedback) {
        noteToCancelOrComplete.noteContent = specialistFeedback;
      }
      noteToCancelOrComplete.kpis = kpis[1].kpiComment ? kpis : undefined;
      const updatedCaseDetails: CaseDetails = {
        ...caseDetails,
        statusCd: NoteStatusCodes.closed,
        statusReason:
          actionType === 'complete' ? NoteStatusReasons.closed : NoteStatusReasons.cancelled,
        participant: {
          ...caseDetails?.participant,
          statusCd: NoteStatusCodes.closed,
          statusReason:
            actionType === 'complete'
              ? NoteParticipantStatusReasons.closedCompleted
              : caseDetails?.participant?.statusReason,
          ...(caseDetails?.participant?.triageStatusCd && {
            triageStatusCd: NoteStatusCodes.closed
          })
        },
        notes: [noteToCancelOrComplete]
      };
      setCaseDetails(updatedCaseDetails);
      removeAllErrorNotifications();
      CaseService.upsertCaseDetails(updatedCaseDetails);
      addNotification(NotificationFactory.createNotification('success', getNotificationMessage()));
      setIsCompleteBtnDisabled(false);
      setSelectedTab(undefined);
      setTimeout(() => {
        if (providerType === 'search') {
          queryClient.invalidateQueries(['caseSearch']);
        } else {
          queryClient.invalidateQueries(['caseResults']);
        }
      }, 1000);
      if (navigationAction && navigationAction.navigateToUrl) {
        const url = navigationAction.navigateToUrl;
        setNavigationAction(undefined);
        navigate(url);
      } else {
        const urlToNavigate = CaseService.getReturnNavigationUrl(providerType, statusType, user);
        navigate(urlToNavigate.toLowerCase());
      }
    }
  };
  const handleContactConsentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setContactConsent(event.target.checked);
    if ((event.target.checked || feedbackConsent) && !programFeedback) {
      setProgramFeedbackErrorMessageStyle(inVaildFieldMessageType);
    } else {
      setProgramFeedbackErrorMessageStyle(validFieldMessageType);
    }
  };
  const handleFeedbackConsentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFeedbackConsent(event.target.checked);
    if ((contactConsent || event.target.checked) && !programFeedback) {
      setProgramFeedbackErrorMessageStyle(inVaildFieldMessageType);
    } else {
      setProgramFeedbackErrorMessageStyle(validFieldMessageType);
    }
  };
  const onDraftWarningDialogCancelled = () => {
    setIsDraftWarningDialogOpen(false);
    setDraftNote(undefined);
    setCaseDetails(undefined);
    abortAllUploads();
    navigate(-1); // Allow navigation back
  };
  const onDraftWarningDialogConfirmed = () => {
    setIsDraftWarningDialogOpen(false);
    window.history.pushState(null, '', window.location.href);
  };
  const handleCompleteTextChanged = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setSpecialistFeedback(event.target.value);
    setSpecialistFeedbackChanged(true);
    const draftFinishNote = getDrafFinishtNote();
    if (draftFinishNote) {
      setDraftNote({
        ...draftFinishNote,
        noteContent: event.target.value
      });
    }
  };
  return (
    <>
      {isAssignerCancel && actionType === 'cancel' && (
        <>
          <Typography variant="body1b">{t('Case.consult.assignerCancel')}</Typography>
          <Typography variant="body2" mt={1}>
            {t('Case.consult.assignerCancel2')}
          </Typography>
          <EconsultActionButton
            sx={{ mt: 1, mb: 3 }}
            variant="contained"
            onClick={handleAssignerCancel}
            disabled={isCompleteBtnDisabled}>
            {t('Case.button.ok')?.toUpperCase()}
          </EconsultActionButton>
        </>
      )}
      {(!isAssignerCancel || actionType === 'complete') && (
        <>
          {actionType === 'complete' && (
            <EconsultTextField
              value={specialistFeedback}
              onChange={handleCompleteTextChanged}
              multiline
              rows={3}
              fullWidth
              placeholder={t('Case.consult.provideCommentsOrFeedback') as string}
              inputProps={{
                style: {
                  resize: 'vertical',
                  overflow: 'auto'
                }
              }}
            />
          )}
          <EconsultTextField
            sx={{ mt: 3 }}
            value={programFeedback}
            onChange={(event) => {
              setProgramFeedback(event.target.value);
              if (event.target.value) {
                setProgramFeedbackErrorMessageStyle(validFieldMessageType);
              } else if (contactConsent || feedbackConsent) {
                setProgramFeedbackErrorMessageStyle(inVaildFieldMessageType);
              }
            }}
            multiline
            rows={3}
            fullWidth
            placeholder={t('Case.consult.provideCommentsOrFeedbackProgram') as string}
            inputProps={{
              maxLength: 4000,
              style: {
                resize: 'vertical',
                overflow: 'auto'
              }
            }}
          />
          <Typography
            id="programFeedbackValidationdMessage"
            style={programFeedbackErrorMessageStyle}>
            {t('Case.patient.field.required')}
          </Typography>
          <FormGroup sx={{ mt: 1 }} onChange={handleContactConsentChange}>
            <Box display="flex" alignItems="center">
              <CheckBoxOptionControl
                sx={{ mr: 0 }}
                control={<Checkbox checked={contactConsent} name="noOhip" />}
                label=""
                disableTypography
              />
              <Box component="span" sx={{ ml: 0 }}>
                {t('Case.consult.contactConsent')}
              </Box>
            </Box>
          </FormGroup>
          <FormGroup onChange={handleFeedbackConsentChange}>
            <Box display="flex" alignItems="center">
              <CheckBoxOptionControl
                sx={{ mr: 0 }}
                control={<Checkbox checked={feedbackConsent} name="noOhip" />}
                label=""
                disableTypography
              />
              <Box component="span" sx={{ ml: 0 }}>
                {t('Case.consult.feedbackConsent')}
              </Box>
            </Box>
          </FormGroup>
          <Box>
            {actionType === 'complete' && (
              <Typography variant="body4">{t('Case.consult.consentNote')}</Typography>
            )}
            {actionType === 'complete' && (
              <>
                <br />
                <Typography variant="body4">{t('Case.consult.consentNote2')}</Typography>
              </>
            )}
            {actionType !== 'complete' && (
              <Typography variant="body4">{t('Case.consult.cancelNote')}</Typography>
            )}
          </Box>
          <EconsultActionButton
            sx={{ mt: 1, mb: 3 }}
            variant="contained"
            onClick={handleComplete}
            disabled={isCompleteBtnDisabled}>
            {actionType === 'complete' ? t('Case.consult.complete') : t('Case.consult.cancel')}
          </EconsultActionButton>
        </>
      )}
      <EconsultDraftLostWarningDialog
        isOpen={isDraftWarningDialogOpen}
        onLeave={onDraftWarningDialogCancelled}
        onStay={onDraftWarningDialogConfirmed}
      />
    </>
  );
};

export default EconsultCaseComplete;
